import type { RoiId } from '~/domain/changes/ids';
import {
    ImageNotHuQuantifiedError,
    ImageNotSuvQuantifiedError,
} from '~/domain/errors/DomainError';
// Type-only: the eligibility gate takes the domain image, but this module
// must not pull the Image3D runtime graph into headless unit tests.
import type { Image3D } from '~/domain/patient/image3d';
import {
    VoiShape,
    gridSubRange,
    subRangeIsFullGrid,
} from '~/domain/services/imageService/gridGeometry';
// Type-only: this module stays free of the engine's runtime import
// graph so the node-environment unit tests can load it headlessly.
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import type { Environment } from '~/engine/data/Environment';
import {
    BodyContourMaskParams,
    BodyContourMaskResult,
    BodyContourTextureStages,
    extractBodyContourMask,
} from '~/engine/util/algorithms/sdf/BodyContourExtraction';
import { getSigmaForStrength } from '~/engine/util/algorithms/sdf/GaussianSmoothing';
import { Grid } from '~/engine/data/common/Grid';
import { VoiBounds } from '~/state/atoms/toolsAtoms';

/**
 * Panel-level threshold-segmentation parameters (SR-WOR-H-27-2; Body
 * preset specifics SR-WOR-H-27-1). Enable flags and values are kept
 * separate so toggling a stage off and back on restores the user's
 * value; the engine receives only the effective values (see
 * {@link toBodyContourMaskStages} and {@link toBodyContourTextureStages}).
 */
export interface BodyContourParams {
    /** Preset (tab) the parameters belong to. Only part of the PREVIEW
     *  key — the mask pipeline is preset-agnostic, so identical mask
     *  parameters may share the cached mask across tabs. */
    presetId: string;
    thresholdHu: number;
    /**
     * Inclusive UPPER HU bound of a BANDED preset (Bone): a voxel is in
     * the structure iff `thresholdHu ≤ HU ≤ upperThresholdHu`. Undefined
     * for the single-sided presets (Body, High density), which include
     * everything at or above the threshold — and which therefore key,
     * compute and record exactly as they did before banding existed.
     */
    upperThresholdHu?: number;
    /** Per-slice (2D) keep-largest stage — runs on the smoothed field. */
    keepLargest2dEnabled: boolean;
    keepLargest2dCount: number;
    /** Whole-volume (3D) keep-largest stage; runs after the 2D stage. */
    keepLargest3dEnabled: boolean;
    keepLargest3dCount: number;
    /** Min-size stage — runs on the smoothed field, before the keeps. */
    minPartSizeEnabled: boolean;
    /**
     * Min-size floor as per-slice in-plane area in mm² (domain unit).
     * The panel displays and accepts cm² and converts at the UI
     * boundary — every value from here down is mm².
     */
    minPartAreaMm2: number;
    /** Per-slice cavity fill (mask phase). The Body preset locks it true
     *  (not exposed); Bone/High density expose it, default off. */
    fillHoles2dEnabled: boolean;
    smoothingEnabled: boolean;
    /**
     * Continuous smoothing-strength position, 1..MAX_SMOOTHING_LEVEL,
     * quantized to 0.1 by the panel's slider. Not a level index — the
     * engine receives the sigma this position maps to (see
     * {@link toBodyContourTextureStages} and {@link getSigmaForStrength}).
     */
    smoothingStrength: number;
    /** Final per-slice cavity re-fill AFTER the cleanup stages (Body
     *  locks it on; not exposed by any preset). */
    finalFillHoles2dEnabled: boolean;
    /** Symmetric margin in mm applied after the cleanup stages (High
     *  density preset); 0 = no margin stage. */
    marginMm: number;
    /**
     * Keep-inside-EXTERNAL stage: identity + content version of the
     * EXTERNAL structure the result is intersected with, or null when
     * no usable EXTERNAL exists (the stage is implicit — there is no
     * user flag; see {@link roiHasEngineGeometry}). The sdfVersion ties the
     * PREVIEW key to the clip target's content, so an edited EXTERNAL
     * marks a held preview stale (best-effort: the version is sampled
     * on render, so an edit between renders shows up one render later).
     * The hooks resolve the actual texture from the engine at compute
     * time — a texture object must never sit in a serializable key.
     */
    keepInsideExternal: { roiId: RoiId; sdfVersion: number } | null;
    /**
     * World-space restriction box (the shared VOI overlay's bounds), or
     * null when "Restrict to region" is off. Only the voxel window the
     * box snaps to reaches the engine and the cache key — sub-voxel
     * handle jitter neither changes the result nor busts the cache —
     * and a full-volume box is normalized to "no restriction" (see
     * {@link boundsKeyToken}).
     */
    boundsMm: VoiBounds | null;
    /**
     * Body the restriction occupies within {@link boundsMm} — the box
     * itself, or the ellipsoid inscribed in it. Absent means `'box'`, so
     * a preset that never offers the choice keys and computes exactly as
     * it did before the shape existed.
     */
    regionShape?: VoiShape;
}

type BodyContourMaskStages = Pick<BodyContourMaskParams, 'fillCavities2d'>;

/**
 * Effective CPU mask-phase stage parameters. Only the cavity fill
 * remains here — every cleanup stage (min-size, keep-largest, final
 * fill) runs after smoothing, on the GPU-processed field.
 */
function toBodyContourMaskStages(p: BodyContourParams): BodyContourMaskStages {
    return {
        fillCavities2d: p.fillHoles2dEnabled,
    };
}

/**
 * Effective GPU/field-phase parameters: a disabled stage is simply
 * omitted. The keep-inside texture is NOT resolved here — the hooks
 * look it up from the engine at compute time (see
 * {@link BodyContourParams.keepInsideExternal}).
 */
export function toBodyContourTextureStages(
    p: BodyContourParams
): BodyContourTextureStages {
    return {
        // The strength POSITION is a UI coordinate; the engine takes the
        // physical parameter it maps to, resolved once here so the
        // pipeline and the provenance record read the same sigma.
        smoothingSigma: p.smoothingEnabled
            ? getSigmaForStrength(p.smoothingStrength)
            : undefined,
        minPartAreaMm2: p.minPartSizeEnabled ? p.minPartAreaMm2 : undefined,
        keepLargest2dCount: p.keepLargest2dEnabled
            ? p.keepLargest2dCount
            : undefined,
        keepLargest3dCount: p.keepLargest3dEnabled
            ? p.keepLargest3dCount
            : undefined,
        finalFillHoles2d: p.finalFillHoles2dEnabled ? true : undefined,
        marginMm: p.marginMm > 0 ? p.marginMm : undefined,
    };
}

/**
 * Resolve the live engine texture of the keep-inside EXTERNAL at compute
 * time (shared by the preview and apply hooks). Returns undefined when
 * the stage is off. Fails fast when the stage is REQUESTED but the
 * texture cannot be resolved (structure deleted / contours cleared since
 * the panel rendered): silently skipping an enabled clip stage would
 * deliver a result larger than the user configured — the SaMD hazard is
 * the wrong result presented as valid, not the failed compute.
 */
export function resolveKeepInsideTexture(
    environment: Environment | null,
    p: BodyContourParams
): ContourTexture | undefined {
    if (p.keepInsideExternal === null) {
        return undefined;
    }
    const texture = environment?.structureSet?.getRoi(
        p.keepInsideExternal.roiId
    )?.texture;
    if (!texture) {
        throw new Error(
            'The EXTERNAL structure to keep the result inside no longer has contours'
        );
    }
    return texture;
}

/**
 * Whether an ROI actually holds geometry. The keep-inside stage asks it
 * of its EXTERNAL (one without geometry would clip the result away to
 * nothing) and the union asks it of every source, so the predicate is
 * named for what it answers rather than for either caller.
 *
 * The question is asked of the ENGINE, not of the domain read model.
 * A structure this tool has just created carries its geometry as an
 * engine texture (`replaceTexture`); the domain contour array stays
 * empty until `StructureSetObject.saveChanges()` writes it back. Gating
 * on the domain `contourCount` alone therefore reported "no EXTERNAL"
 * for a Body generated moments earlier in the same panel session, while
 * a DICOM-loaded one worked.
 *
 * The engine answer is also the CORRECT one to gate on: the clip stage
 * consumes exactly this texture (see {@link resolveKeepInsideTexture}),
 * so the gate and the compute can never disagree. The domain count is
 * the fallback for the case where the engine knows nothing of the ROI
 * (no environment, or the ROI not mirrored into the engine yet).
 *
 * @param domainContourCount Contour count from the domain read model.
 */
export function roiHasEngineGeometry(
    environment: Environment | null,
    roiId: RoiId,
    domainContourCount: number
): boolean {
    const engineRoi = environment?.structureSet?.getRoi(roiId);
    if (!engineRoi) {
        return domainContourCount > 0;
    }
    // A point ROI has no texture at all, so the clip stage could not run
    // against it whatever the domain says.
    const texture = engineRoi.texture;
    return texture !== undefined && !texture.isEmpty;
}

/**
 * Voxel-unit eligibility of a threshold tool: the domain verdict the UI
 * hooks gate on, and the i18n id of the refusal they surface. The
 * preview/apply hooks default to {@link THRESHOLD_TOOL_HU_ELIGIBILITY}
 * (the CT tool); the SUV tool passes its own — one config object per
 * tool, never a code fork in the hooks.
 */
/**
 * Voxel unit a {@link BodyContourMaskCache} instance requires of its
 * image input before any compute — 'hu' (the CT tool, the default) or
 * 'suv' (the SUV-threshold tool).
 */
export type ThresholdQuantification = 'hu' | 'suv';

export interface ThresholdToolEligibility {
    isQuantified: (image: Image3D) => boolean;
    refusalMessageId: string;
}

/** The CT tool's gate: HU-quantified, refusal `thresholdSeg.notCt`. */
export const THRESHOLD_TOOL_HU_ELIGIBILITY: ThresholdToolEligibility = {
    isQuantified: (image: Image3D) => image.isHuQuantified,
    refusalMessageId: 'thresholdSeg.notCt',
};

/**
 * Key identity of the scan a mask or preview belongs to — the only part
 * of the image the cache KEYS depend on. Satisfied by the domain
 * {@link Image3D}, which is also what the compute itself takes: the
 * eligibility verdict and the pixel data are read from the image there,
 * never accepted as caller-supplied fields (see
 * {@link BodyContourMaskCache.getOrExtract}).
 */
export interface BodyContourImageIdentity {
    dicomUid: string;
    grid: Grid;
}

/**
 * Restriction-region key token. Empty for no box AND for a full-volume
 * BOX — the two run the identical engine path (the extraction normalizes
 * a full-volume box away), so they must share a cache entry. A
 * restricting region contributes its voxel window, not its raw mm
 * coordinates: the engine only ever sees the snapped window, so
 * sub-voxel handle jitter must not bust the cache.
 *
 * The shape contributes whenever it is not the default box: the same
 * window under the two shapes selects different voxels, so they must
 * never share an entry. A full-volume ELLIPSOID keys (and computes) as a
 * restriction — it still excludes the volume's corners.
 */
function boundsKeyToken(
    grid: Grid,
    boundsMm: VoiBounds | null,
    regionShape: VoiShape | undefined
): string {
    if (boundsMm === null) {
        return '';
    }
    const shape = regionShape ?? 'box';
    const r = gridSubRange(grid, boundsMm);
    if (shape === 'box' && subRangeIsFullGrid(grid, r)) {
        return '';
    }
    const shapeToken = shape === 'box' ? '' : `,${shape}`;
    return `|b:${r.minX}-${r.maxX},${r.minY}-${r.maxY},${r.minZ}-${r.maxZ}${shapeToken}`;
}

/**
 * Upper-inclusion-bound key token. EMPTY for an unbounded run — both an
 * absent bound and an explicit +Infinity, which the extraction treats
 * identically — so every single-sided preset keys exactly as it did
 * before banding existed and no existing cache entry is invalidated. Any
 * other value contributes, including a non-finite one other than
 * +Infinity: a NaN bound classifies every voxel out of range, which is
 * emphatically not the unbounded mask and must never share its entry.
 */
function upperThresholdKeyToken(upperThresholdHu: number | undefined): string {
    if (
        upperThresholdHu === undefined ||
        upperThresholdHu === Number.POSITIVE_INFINITY
    ) {
        return '';
    }
    return `|u:${upperThresholdHu}`;
}

/**
 * Cache key over exactly the inputs the CPU mask pipeline depends on —
 * threshold band, cavity-fill flag and restriction window. The field-phase
 * parameters (smoothing, min-size, keep-largest, final fill, margin,
 * keep-inside) and the preset id are deliberately absent: changing them
 * must serve the cached mask. The image uid is part of the key so a
 * primary-image switch can never serve a mask computed from another
 * scan.
 */
function bodyContourMaskKey(
    image: BodyContourImageIdentity,
    p: BodyContourParams
): string {
    const fill = p.fillHoles2dEnabled ? 'on' : 'off';
    return (
        `body-contour:${image.dicomUid}|${p.thresholdHu}` +
        upperThresholdKeyToken(p.upperThresholdHu) +
        `|fill:${fill}` +
        boundsKeyToken(image.grid, p.boundsMm, p.regionShape)
    );
}

/** Preview key: the mask key plus the field-phase parameters (smoothing,
 *  min-size, keep-largest, final fill, margin, keep-inside) and the
 *  preset id — so switching tabs always marks a held preview stale. */
export function bodyContourPreviewKey(
    image: BodyContourImageIdentity,
    p: BodyContourParams
): string {
    // The strength POSITION, not the sigma: the mapping is strictly
    // increasing, so the two identify each other, and the position is
    // already 0.1-quantized — a 0.1 nudge therefore busts the key while
    // sub-quantum float noise cannot.
    const smooth = p.smoothingEnabled ? p.smoothingStrength : 'off';
    const minSize = p.minPartSizeEnabled ? p.minPartAreaMm2 : 'off';
    const keep2d = p.keepLargest2dEnabled ? p.keepLargest2dCount : 'off';
    const keep3d = p.keepLargest3dEnabled ? p.keepLargest3dCount : 'off';
    const finalFill = p.finalFillHoles2dEnabled ? 'on' : 'off';
    const margin = p.marginMm > 0 ? p.marginMm : 'off';
    // The keep-inside token derives from the clip target's EXISTENCE
    // (roiId + content version), never from a user flag: the stage
    // is implicit. 'none' is the no-usable-EXTERNAL token: a preview
    // taken while none existed can therefore never be reused once one
    // does, and vice versa.
    const inExt = p.keepInsideExternal
        ? `${p.keepInsideExternal.roiId}@${p.keepInsideExternal.sdfVersion}`
        : 'none';
    return (
        `${bodyContourMaskKey(image, p)}` +
        `|smooth:${smooth}|min:${minSize}|k2:${keep2d}|k3:${keep3d}` +
        `|ff:${finalFill}|margin:${margin}|inExt:${inExt}` +
        `|preset:${p.presetId}`
    );
}

/**
 * Single-entry cache for the CPU mask pipeline, so parameter changes that
 * only affect the field phase (smoothing, min-size, keep-largest, final
 * fill, margin, keep-inside — levels and enables) skip the multi-second
 * threshold → fill recompute entirely. One entry is
 * deliberate: it bounds retained memory to one mask volume (~1 byte/voxel),
 * released when the owner drops the cache (panel unmount) or clears it
 * explicitly (primary-image switch — the old scan's entry can never be
 * served again, its key embeds the image uid).
 */
export class BodyContourMaskCache {
    private entry: { key: string; mask: BodyContourMaskResult } | null = null;

    /**
     * @param quantification Voxel unit this cache's tool thresholds in;
     *   decides which domain verdict the compute seam re-checks and which
     *   typed error it throws. Defaults to 'hu' (the CT tool).
     */
    constructor(
        private readonly quantification: ThresholdQuantification = 'hu'
    ) {}

    public async getOrExtract(
        image: Image3D,
        params: BodyContourParams,
        signal?: AbortSignal
    ): Promise<BodyContourMaskResult> {
        // Fail fast BEFORE any cache lookup or compute: the UI gating
        // (toolbar button, panel refusal, hook guards) makes this
        // unreachable in normal operation, but a stale invocation that
        // slipped past it would threshold raw unquantified voxel values
        // into a plausible-looking wrong structure — the SaMD hazard is
        // the wrong result presented as valid, so the compute boundary
        // re-checks rather than trusts.
        if (this.quantification === 'suv') {
            // `!== true` also refuses an input that never carried the
            // verdict (the optional field): absent is not quantified.
            if (image.isSuvQuantified !== true) {
                throw new ImageNotSuvQuantifiedError(
                    'SUV thresholding requires a PET image quantified ' +
                        'as SUVbw: activity concentration in BQML decay-' +
                        'corrected to scan start, or a pre-converted GML ' +
                        'series, attenuation- and decay-corrected, with ' +
                        'patient weight, injected dose and timing present'
                );
            }
        } else if (!image.isHuQuantified) {
            throw new ImageNotHuQuantifiedError(
                'Threshold segmentation requires a CT image quantified ' +
                    'in Hounsfield Units'
            );
        }
        const key = bodyContourMaskKey(image, params);
        if (this.entry?.key === key) {
            return this.entry.mask;
        }
        const mask = await extractBodyContourMask({
            imageGrid: image.grid,
            floatPixelData: image.floatPixelData,
            thresholdHu: params.thresholdHu,
            upperThresholdHu: params.upperThresholdHu,
            boundsMm: params.boundsMm ?? undefined,
            regionShape: params.regionShape,
            ...toBodyContourMaskStages(params),
            signal,
        });
        // Cache only completed extractions — an abort throws before this.
        this.entry = { key, mask };
        return mask;
    }

    public clear(): void {
        this.entry = null;
    }
}
